export type NtvBlackoutRule = {
  type: string;
  value: string;
  daysBefore: number | null;
  daysAfter: number | null;
};

/** luxon Monday=1…Sunday=7 → ResMan DATEPART weekday Sunday=1…Saturday=7 */
function luxonWeekdayToResman(weekday: number): number {
  return weekday === 7 ? 1 : weekday + 1;
}

function parseLocalDate(isoDate: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (
    d.getFullYear() !== Number(m[1]) ||
    d.getMonth() !== Number(m[2]) - 1 ||
    d.getDate() !== Number(m[3])
  ) {
    return null;
  }
  return d;
}

export function isLeaseExpirationBlackout(
  isoDate: string,
  rules: NtvBlackoutRule[]
): boolean {
  const d = parseLocalDate(isoDate);
  if (!d) return false;
  // JS getDay: Sunday=0…Saturday=6 → convert to luxon weekday Mon=1…Sun=7
  const jsDay = d.getDay();
  const luxonWeekday = jsDay === 0 ? 7 : jsDay;
  for (const rule of rules) {
    if (rule.type === "DayOfMonth" && d.getDate() === Number(rule.value)) {
      return true;
    }
    if (
      rule.type === "DayOfWeek" &&
      luxonWeekdayToResman(luxonWeekday) === Number(rule.value)
    ) {
      return true;
    }
  }
  return false;
}

export function minMoveOutDateIso(
  noticeDays: number,
  now = new Date()
): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() + noticeDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function validateNtvMoveOutDate(input: {
  moveOutDate: string;
  noticeDays: number;
  blackouts: NtvBlackoutRule[];
  now?: Date;
}): "ok" | "invalid_date" | "too_soon" | "blackout" {
  const move = parseLocalDate(input.moveOutDate);
  if (!move) return "invalid_date";
  const minIso = minMoveOutDateIso(input.noticeDays, input.now);
  const min = parseLocalDate(minIso)!;
  if (move < min) return "too_soon";
  if (isLeaseExpirationBlackout(input.moveOutDate, input.blackouts)) {
    return "blackout";
  }
  return "ok";
}

export type NtvDraft = {
  moveOutDate: string;
  allLeaving: boolean | null;
  personIds: string[];
  reasonId: string;
  forwardingAddress: string;
};

export function ntvDraftKey(leaseId: string) {
  return `ntvDraft:${leaseId}`;
}

export function loadNtvDraft(leaseId: string): NtvDraft | null {
  try {
    const raw = sessionStorage.getItem(ntvDraftKey(leaseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NtvDraft>;
    return {
      moveOutDate:
        typeof parsed.moveOutDate === "string" ? parsed.moveOutDate : "",
      allLeaving:
        parsed.allLeaving === true || parsed.allLeaving === false
          ? parsed.allLeaving
          : null,
      personIds: Array.isArray(parsed.personIds)
        ? parsed.personIds.map(String)
        : [],
      reasonId: typeof parsed.reasonId === "string" ? parsed.reasonId : "",
      forwardingAddress:
        typeof parsed.forwardingAddress === "string"
          ? parsed.forwardingAddress
          : "",
    };
  } catch {
    return null;
  }
}

export function saveNtvDraft(leaseId: string, draft: NtvDraft) {
  try {
    sessionStorage.setItem(ntvDraftKey(leaseId), JSON.stringify(draft));
  } catch {
    // ignore
  }
}

export function clearNtvDraft(leaseId: string) {
  try {
    sessionStorage.removeItem(ntvDraftKey(leaseId));
  } catch {
    // ignore
  }
}
